import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import {
  pushSupported,
  useCurrentPushSubscription,
  usePushKey,
  useSubscribeToPush,
  useUnsubscribeFromPush,
} from '../hooks/usePush'

/**
 * Abonnement du navigateur aux notifications push. Le bouton n'apparaît que si
 * le navigateur sait les recevoir et si le serveur a des clés : proposer un
 * réglage sans effet serait pire que de ne rien proposer.
 */
export function PushToggle() {
  const { t } = useTranslation()
  const { data: key } = usePushKey()
  const { data: subscription } = useCurrentPushSubscription()
  const subscribe = useSubscribeToPush(key?.public_key ?? null)
  const unsubscribe = useUnsubscribeFromPush()

  if (!pushSupported()) {
    return (
      <section className="flex flex-col gap-2">
        <h2 className="font-display text-lg font-semibold text-on-surface">
          {t('notifications.push.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('notifications.push.unsupported')}</p>
      </section>
    )
  }

  if (key && !key.enabled) {
    return (
      <section className="flex flex-col gap-2">
        <h2 className="font-display text-lg font-semibold text-on-surface">
          {t('notifications.push.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('notifications.push.disabled')}</p>
      </section>
    )
  }

  const isSubscribed = subscription != null

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="font-display text-lg font-semibold text-on-surface">
          {t('notifications.push.title')}
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">{t('notifications.push.hint')}</p>
      </div>

      {subscribe.isError && (
        <Alert variant="warning">
          {subscribe.error instanceof Error && subscribe.error.message === 'denied'
            ? t('notifications.push.denied')
            : t('notifications.push.error')}
        </Alert>
      )}

      {isSubscribed ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-secondary">
            {t('notifications.push.active')}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => unsubscribe.mutate()}
            isLoading={unsubscribe.isPending}
          >
            {t('notifications.push.disable')}
          </Button>
        </div>
      ) : (
        <div>
          <Button onClick={() => subscribe.mutate()} isLoading={subscribe.isPending}>
            {t('notifications.push.enable')}
          </Button>
        </div>
      )}
    </section>
  )
}
